import { useState, useCallback } from 'react';
import { TopicSentence } from '../types';
import { useKeyboardShortcuts } from '../../../hooks/useKeyboardShortcuts';

interface Props {
  sentences: TopicSentence[];
  onStartDictation: (checks: Set<number>) => void;
}

export default function PreviewPhase({ sentences, onStartDictation }: Props) {
  const [checks, setChecks] = useState<Set<number>>(new Set());
  const [expanded, setExpanded] = useState<number | null>(null);
  const [focusIdx, setFocusIdx] = useState(0);

  const toggleCheck = (index: number) => {
    setChecks((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  // 键盘导航
  const moveFocusUp = useCallback(() => {
    setFocusIdx((prev) => Math.max(0, prev - 1));
  }, []);

  const moveFocusDown = useCallback(() => {
    setFocusIdx((prev) => Math.min(sentences.length - 1, prev + 1));
  }, [sentences.length]);

  const toggleCurrentCheck = useCallback(() => {
    if (sentences[focusIdx]) {
      toggleCheck(sentences[focusIdx].index);
    }
  }, [focusIdx, sentences]);

  const toggleCurrentExpand = useCallback(() => {
    if (sentences[focusIdx]) {
      const idx = sentences[focusIdx].index;
      setExpanded((prev) => (prev === idx ? null : idx));
    }
  }, [focusIdx, sentences]);

  const startDictation = useCallback(() => {
    onStartDictation(checks);
  }, [checks, onStartDictation]);

  // 快捷键
  useKeyboardShortcuts({
    shortcuts: {
      'arrowup': moveFocusUp,
      'arrowdown': moveFocusDown,
      'k': moveFocusUp,
      'j': moveFocusDown,
      ' ': toggleCurrentCheck,  // Space 勾选
      'enter': toggleCurrentExpand,  // Enter 展开/折叠
      'tab': startDictation,  // Tab 开始默写
    },
  });

  const ratio = sentences.length > 0 ? checks.size / sentences.length : 0;

  return (
    <div className="topic-recall-content">
      <ul className="topic-recall-list">
        {sentences.map((sentence, idx) => (
          <li
            key={sentence.index}
            className={`topic-recall-item-wrapper ${idx === focusIdx ? 'focused' : ''}`}
          >
            <div className="topic-recall-row">
              <label className="topic-recall-checkbox">
                <input
                  type="checkbox"
                  checked={checks.has(sentence.index)}
                  onChange={() => toggleCheck(sentence.index)}
                  tabIndex={-1}
                />
              </label>
              <button
                type="button"
                className={`topic-recall-item ${expanded === sentence.index ? 'expanded' : ''}`}
                onClick={() => setExpanded(expanded === sentence.index ? null : sentence.index)}
                tabIndex={-1}
              >
                <span className="topic-recall-text">{sentence.chinese}</span>
                <span className="topic-recall-expand-icon">
                  {expanded === sentence.index ? '▼' : '▶'}
                </span>
              </button>
            </div>
            {expanded === sentence.index && (
              <div className="topic-recall-english">{sentence.english}</div>
            )}
          </li>
        ))}
      </ul>

      <div className="topic-recall-ratio">
        <span>想起 {checks.size}/{sentences.length}</span>
        {ratio >= 0.8 && <span className="pill success">很好</span>}
        {ratio >= 0.5 && ratio < 0.8 && <span className="pill warn">一般</span>}
        {ratio < 0.5 && sentences.length > 0 && <span className="pill danger">需加强</span>}
      </div>

      <div className="topic-preview-hints">
        <span className="hint-item">↑↓ 移动</span>
        <span className="hint-item">Space 勾选</span>
        <span className="hint-item">Enter 展开</span>
      </div>

      <button
        type="button"
        className="topic-recall-reveal-btn"
        onClick={() => onStartDictation(checks)}
      >
        开始默写 → (Tab)
      </button>
    </div>
  );
}
